import { useEffect, useState } from "react";
import { useParams, useNavigate, Link, useLocation } from "react-router-dom";
import { api, formatApiError, ENABLE_MOCK_PAYMENTS } from "../lib/api";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Loader2, ShieldCheck, ArrowLeft } from "lucide-react";

const RAZORPAY_SDK_URL = "https://checkout.razorpay.com/v1/checkout.js";

function loadRazorpaySdk() {
  if (window.Razorpay) {
    return Promise.resolve(window.Razorpay);
  }

  return new Promise((resolve, reject) => {
    const existing = document.querySelector(`script[src="${RAZORPAY_SDK_URL}"]`);
    if (existing) {
      existing.addEventListener("load", () => resolve(window.Razorpay), {
        once: true,
      });
      existing.addEventListener(
        "error",
        () => reject(new Error("Failed to load Razorpay checkout")),
        { once: true }
      );
      return;
    }

    const script = document.createElement("script");
    script.src = RAZORPAY_SDK_URL;
    script.async = true;
    script.onload = () => resolve(window.Razorpay);
    script.onerror = () => reject(new Error("Failed to load Razorpay checkout"));
    document.body.appendChild(script);
  });
}

function openRazorpayCheckout(order) {
  return new Promise((resolve, reject) => {
    let failureMessage = "";
    const rzp = new window.Razorpay({
      // The backend returns the key of the Razorpay account the order was
      // created on: the organizer's own account when they have connected
      // one, otherwise the platform account. Checkout must use that same
      // key or Razorpay rejects the order id.
      key: order.key_id,
      order_id: order.order_id,
      amount: order.amount,
      currency: order.currency,
      name: "GrowithEvent",
      description: order.event_name,
      prefill: order.prefill,
      theme: { color: "#000000" },
      handler: (response) => resolve(response),
      modal: {
        ondismiss: () => {
          const error = new Error(failureMessage || "Payment cancelled");
          error.cancelled = !failureMessage;
          reject(error);
        },
      },
    });
    rzp.on("payment.failed", (response) => {
      failureMessage =
        response?.error?.description || "Payment failed. Please try again.";
      toast.error(failureMessage);
    });
    rzp.open();
  });
}

export default function Payment() {
  const { registrationId } = useParams();
  const [data, setData] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [verificationAttempted, setVerificationAttempted] = useState(false);
  const [loadError, setLoadError] = useState("");
  const nav = useNavigate();
  const location = useLocation();
  const returnedOrderId = new URLSearchParams(location.search).get(
    "cashfree_order_id"
  );

  useEffect(() => {
    api
      .get(`/registrations/${registrationId}`)
      .then((response) => {
        if (response.data.registration.payment_status === "paid") {
          nav(`/ticket/${registrationId}`, { replace: true });
          return;
        }
        setData(response.data);
      })
      .catch((error) => setLoadError(formatApiError(error)));
  }, [registrationId, nav]);

  useEffect(() => {
    if (
      ENABLE_MOCK_PAYMENTS ||
      !data ||
      !returnedOrderId ||
      verifying ||
      verificationAttempted
    ) {
      return;
    }

    setVerifying(true);
    setVerificationAttempted(true);
    api
      .post(`/registrations/${registrationId}/cashfree-verify`, {
        order_id: returnedOrderId,
      })
      .then((response) => {
        const status = response.data.status;
        if (status === "paid" || status === "already_paid") {
          toast.success("Payment successful");
          nav(`/ticket/${registrationId}`, { replace: true });
          return;
        }
        toast.error("Payment not completed. Please try again.");
        nav(`/pay/${registrationId}`, { replace: true });
      })
      .catch((error) => {
        toast.error(formatApiError(error));
        nav(`/pay/${registrationId}`, { replace: true });
      })
      .finally(() => setVerifying(false));
  }, [
    data,
    nav,
    registrationId,
    returnedOrderId,
    verificationAttempted,
    verifying,
  ]);

  const pay = async () => {
    if (processing || verifying) {
      return;
    }

    setProcessing(true);

    if (ENABLE_MOCK_PAYMENTS) {
      setTimeout(async () => {
        try {
          await api.post(`/registrations/${registrationId}/mock-payment`);
          toast.success("Payment successful");
          nav(`/ticket/${registrationId}`, { replace: true });
        } catch (error) {
          const message = formatApiError(error);
          nav(
            `/pay/${registrationId}/failed?reason=${encodeURIComponent(message)}`,
            { replace: true }
          );
        } finally {
          setProcessing(false);
        }
      }, 1800);
      return;
    }

    try {
      const response = await api.post(
        `/registrations/${registrationId}/razorpay-order`
      );
      if (response.data.status === "already_paid") {
        nav(`/ticket/${registrationId}`, { replace: true });
        return;
      }
      if (!response.data.key_id || !response.data.order_id) {
        throw new Error("Payment is not set up for this event yet. Please contact the organizer.");
      }

      await loadRazorpaySdk();
      if (typeof window.Razorpay !== "function") {
        throw new Error("Razorpay checkout unavailable");
      }

      const paymentResponse = await openRazorpayCheckout(response.data);

      setVerifying(true);
      const verifyResponse = await api.post(
        `/registrations/${registrationId}/razorpay-verify`,
        {
          razorpay_order_id: paymentResponse.razorpay_order_id,
          razorpay_payment_id: paymentResponse.razorpay_payment_id,
          razorpay_signature: paymentResponse.razorpay_signature,
        }
      );
      const status = verifyResponse.data.status;
      if (status === "paid" || status === "already_paid") {
        toast.success("Payment successful");
        nav(`/ticket/${registrationId}`, { replace: true });
        return;
      }
      throw new Error("Payment verification failed");
    } catch (error) {
      if (error?.cancelled) {
        toast.info("Payment cancelled");
        return;
      }
      const message = formatApiError(error);
      toast.error(message);
      nav(`/pay/${registrationId}/failed?reason=${encodeURIComponent(message)}`, {
        replace: true,
      });
    } finally {
      setProcessing(false);
      setVerifying(false);
    }
  };

  if (loadError) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6">
        <div className="max-w-md w-full border border-zinc-200 rounded-md bg-white p-8 text-center">
          <p className="text-red-600 font-medium mb-4">{loadError}</p>
          <Link to="/">
            <Button variant="outline" className="rounded-md border-zinc-200">
              <ArrowLeft className="w-4 h-4 mr-1.5" /> Back to home
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen flex items-center justify-center text-zinc-500">
        Loading...
      </div>
    );
  }

  const { registration, event } = data;
  const totalAmount = event.price * (registration.ticket_count || 1);

  return (
    <div className="min-h-screen bg-[#FAFAFA] flex items-center justify-center p-6">
      <div className="w-full max-w-md bg-white border border-zinc-200 rounded-md overflow-hidden">
        <div className="p-6 border-b border-zinc-200 flex items-center justify-between">
          <div>
            <p className="overline">
              {ENABLE_MOCK_PAYMENTS ? "Demo checkout" : "Razorpay checkout"}
            </p>
            <p className="font-heading text-lg font-semibold tracking-tight mt-1">
              Secure checkout
            </p>
          </div>
          <ShieldCheck className="w-6 h-6 text-zinc-400" />
        </div>

        <div className="p-6 space-y-4">
          <div className="flex items-center justify-between text-sm">
            <span className="text-zinc-500">Event</span>
            <span className="font-medium text-right max-w-[60%]">
              {event.name}
            </span>
          </div>
          <div className="flex items-center justify-between text-sm">
            <span className="text-zinc-500">Name</span>
            <span className="font-medium">{registration.name}</span>
          </div>
          <div className="flex items-center justify-between text-sm">
            <span className="text-zinc-500">Sponsor</span>
            <span className="font-medium">{registration.sponsor_name}</span>
          </div>
          {registration.ticket_count > 1 && (
            <div className="flex items-center justify-between text-sm">
              <span className="text-zinc-500">Tickets</span>
              <span className="font-medium">
                {registration.ticket_count} × ₹{event.price}
              </span>
            </div>
          )}
          <div className="border-t border-zinc-200 pt-4 flex items-baseline justify-between">
            <span className="overline">Total</span>
            <span
              className="font-heading text-4xl font-semibold tracking-tighter"
              data-testid="payment-amount"
            >
              ₹{totalAmount}
            </span>
          </div>
        </div>

        <div className="p-6 border-t border-zinc-200">
          <Button
            onClick={pay}
            disabled={processing || verifying}
            className="w-full h-12 bg-black text-white hover:bg-zinc-800 rounded-md"
            data-testid="pay-now-button"
          >
            {processing || verifying ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                {verifying ? "Verifying payment..." : "Opening checkout..."}
              </>
            ) : (
              `Pay ₹${totalAmount}`
            )}
          </Button>
          <p className="text-xs text-zinc-400 text-center mt-4">
            {ENABLE_MOCK_PAYMENTS
              ? "Please don't close this tab during payment. This is a simulated payment for demo purposes."
              : "Your payment is processed securely by Razorpay. Please don't close this tab during payment."}
          </p>
        </div>
      </div>
    </div>
  );
}
